// KPI ortak üst bar (M16 T9, kural 9/19): müşteri seçici + set seçici (+ yıl aralığı); seçim
// kpi-store'da global, kullanıcı bazında localStorage'da kalıcı. Mount'ta `?clientId&setId`
// query'sini store'a hydrate eder ve URL'den siler. Kaydedilmemiş değişiklik varken (pendingSaves)
// müşteri/set değişimi onay ister (kural 9). TODO-KPI-06: ≤1024 px'te çubuk sütun olur
// (müşteri / set / yıllar alt alta), sağ yuva tam genişlik — yatay taşma yok.

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { searchKpiClients } from "../../api/kpi";
import {
  KPI_COPY,
  describeDataYears,
  kpiSetLabel,
  kpiYearOptions,
  yearHeaderLabel,
  readKpiQuery,
} from "../../lib/kpi-ui";
import { useMediaQuery } from "../../lib/use-media-query";
import { useAuthStore } from "../../stores/auth-store";
import { useKpiStore } from "../../stores/kpi-store";
import { ClientPicker } from "../common/ClientPicker";
import { ConfirmDialog } from "../common/ConfirmDialog";

const selectStyle: React.CSSProperties = {
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  fontSize: "0.85rem",
  color: "var(--color-text)",
  minWidth: 200,
};

export interface KpiContextBarProps {
  /** set seçici gizlenir (Katalog gibi müşteri-bağımsız ekranlar) */
  hideSet?: boolean;
  /** müşteri seçici gizlenir (Paketler gibi ortak-katalog ekranları); `hideSet` + `right` yoksa çubuk yok */
  hideClient?: boolean;
  /** yıl aralığı seçici */
  showYears?: boolean;
  right?: React.ReactNode;
  onCreateSet?: () => void;
  /** Kural 9: ekran kendi "kaydedilmemiş iş" durumunu bildirir (ör. içe aktarma sihirbazı aktif). */
  lock?: boolean;
  lockTitle?: string;
  lockBody?: string;
}

export function KpiContextBar(props: KpiContextBarProps): JSX.Element | null {
  const {
    hideClient,
    hideSet,
    showYears,
    right,
    onCreateSet,
    lock = false,
    lockTitle,
    lockBody,
  } = props;
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    userId,
    clientId,
    setId,
    clients,
    sets,
    years,
    pendingSaves,
    hydrate,
    selectClient,
    selectSet,
    setYears,
    grid,
  } = useKpiStore();
  // M17 T5 — mali yıl müşterisinde seçenek metni "MY2026" (value yıl sayısı kalır)
  const fiscalStartMonth = grid?.fiscalYearStartMonth;
  const [pendingChange, setPendingChange] = useState<(() => void) | null>(null);
  const tablet = useMediaQuery("(max-width: 1024px)");

  // Mount / kullanıcı değişimi: hydrate (query > localStorage), query temizlenir
  useEffect(() => {
    if (!user) return;
    const q = readKpiQuery(location.search);
    const needs = userId !== user.id || q.clientId !== undefined || q.setId !== undefined;
    if (!needs) return;
    void hydrate({ userId: user.id, query: q }).then(() => {
      if (q.clientId !== undefined || q.setId !== undefined) {
        void navigate(location.pathname, { replace: true });
      }
    });
  }, [user?.id, location.search]);

  function guarded(fn: () => void): void {
    if (pendingSaves > 0 || lock) setPendingChange(() => fn);
    else fn();
  }

  const dataYears = sets.find((x) => x.id === setId)?.dataYears ?? [];
  const yearOptions = kpiYearOptions(new Date().getFullYear(), dataYears, years);

  // müşteri de set de yok, sağ yuva da yok → çubuk hiç render edilmez (Paketler)
  if (hideClient && hideSet && !showYears && !right) return null;

  return (
    <div
      className="flex items-center gap-3 flex-wrap rounded-xl px-3 py-2 mb-4"
      data-testid="kpi-context-bar"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        ...(tablet ? { flexDirection: "column", alignItems: "stretch", gap: 8 } : {}),
      }}
    >
      {/* ClientPicker (2026-10-07): sunucu aramalı combobox; ikincil satır set sayısı /
          "Henüz gösterge seti yok". `<label>` sarmalamaz — çip metni etiketin adına
          karışmasın (erişilebilir ad `aria-label`). */}
      {hideClient ? null : (
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--color-text-2)" }}>
          <span>{KPI_COPY.clientLabel}</span>
          <ClientPicker
            inline
            size="sm"
            ariaLabel={KPI_COPY.clientLabel}
            placeholder={KPI_COPY.notSelected}
            value={
              clientId
                ? { id: clientId, name: clients.find((c) => c.id === clientId)?.name ?? "…" }
                : null
            }
            onChange={(v) => {
              const next = v?.id ?? null;
              if (next === clientId) return;
              guarded(() => void selectClient(next));
            }}
            fetchOptions={searchKpiClients}
          />
        </div>
      )}
      {!hideSet ? (
        <label className="flex items-center gap-2 text-sm" style={{ color: "var(--color-text-2)" }}>
          {KPI_COPY.setLabel}
          {clientId && sets.length === 0 ? (
            onCreateSet ? (
              <button
                type="button"
                onClick={onCreateSet}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold"
                style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
              >
                {KPI_COPY.createSetCta}
              </button>
            ) : (
              <span className="text-sm" style={{ color: "var(--color-text-3)" }}>
                {KPI_COPY.noSetForClient}
              </span>
            )
          ) : (
            <select
              aria-label={KPI_COPY.setLabel}
              style={selectStyle}
              value={setId ?? ""}
              disabled={!clientId}
              onChange={(e) => {
                const v = e.target.value || null;
                guarded(() => void selectSet(v));
              }}
            >
              <option value="">{KPI_COPY.notSelected}</option>
              {sets.map((s) => (
                <option key={s.id} value={s.id}>
                  {kpiSetLabel(s)}
                </option>
              ))}
            </select>
          )}
        </label>
      ) : null}
      {showYears && setId ? (
        <fieldset
          className="flex items-center gap-1 text-sm"
          style={{ border: 0, padding: 0, margin: 0, color: "var(--color-text-2)" }}
        >
          <legend className="sr-only">{KPI_COPY.yearsLabel}</legend>
          <span>{KPI_COPY.yearsLabel}</span>
          {setId ? (
            <span
              className="text-xs"
              style={{ color: "var(--color-text-3)" }}
              title="Bu veri setine yazılmış değer yılları; liste bunları her zaman kapsar"
              data-testid="kpi-data-years"
            >
              ({describeDataYears(dataYears)})
            </span>
          ) : null}
          <select
            aria-label="Başlangıç yılı"
            style={{ ...selectStyle, minWidth: 90 }}
            value={years[0]}
            onChange={(e) => {
              const from = Number(e.target.value);
              const to = years[years.length - 1]!;
              const next: number[] = [];
              for (let y = Math.min(from, to); y <= Math.max(from, to); y += 1) next.push(y);
              void setYears(next);
            }}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {yearHeaderLabel(y, fiscalStartMonth)}
              </option>
            ))}
          </select>
          <span>–</span>
          <select
            aria-label="Bitiş yılı"
            style={{ ...selectStyle, minWidth: 90 }}
            value={years[years.length - 1]}
            onChange={(e) => {
              const to = Number(e.target.value);
              const from = years[0]!;
              const next: number[] = [];
              for (let y = Math.min(from, to); y <= Math.max(from, to); y += 1) next.push(y);
              void setYears(next);
            }}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {yearHeaderLabel(y, fiscalStartMonth)}
              </option>
            ))}
          </select>
        </fieldset>
      ) : null}
      {tablet ? null : <span style={{ flex: 1 }} />}
      {right}
      <ConfirmDialog
        open={pendingChange !== null}
        title={
          pendingSaves > 0 ? KPI_COPY.contextLockTitle : (lockTitle ?? KPI_COPY.contextLockTitle)
        }
        body={pendingSaves > 0 ? KPI_COPY.contextLockBody : (lockBody ?? KPI_COPY.contextLockBody)}
        confirmLabel={KPI_COPY.contextLockConfirm}
        tone="danger"
        onConfirm={() => {
          const fn = pendingChange;
          setPendingChange(null);
          fn?.();
        }}
        onCancel={() => setPendingChange(null)}
      />
    </div>
  );
}
